import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface PriceTrendsHeaderProps {
    locality: any;
    projects: any[];
}

const PriceTrendsHeader: React.FC<PriceTrendsHeaderProps> = ({ locality, projects }) => {
    const projectsCount = projects.length;
    // Grouping by year (Mocking some growth if history is short)
    const yearlyChartData = [2022, 2023, 2024, 2025, 2026].map(year => ({
        name: year,
        price: locality.avgPricePerSqFt * (1 - (2026 - year) * 0.05)
    }));

    // Dynamic Sidebar Data Calculation
    const getBHKPriceRange = (bhks: string) => {
        const filtered = projects.filter(p => 
            p.overview?.configuration?.toLowerCase().includes(bhks.toLowerCase())
        );
        
        if (filtered.length === 0) return "N/A";
        
        // Extract all prices
        const priceStrings = filtered.map(p => p.overview?.priceRange).filter(Boolean);
        if (priceStrings.length === 0) return "On Request";

        // Simple heuristic: just show the range from the first project or a combined min/max if possible
        // For now, let's just take the first one that looks like a range or "Starting from..."
        return priceStrings[0];
    };

    const twoBHKPrice = getBHKPriceRange("2 BHK");

    // Deterministic Rating based on locality name
    const getRating = (name: string) => {
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const rating = 4.0 + (Math.abs(hash) % 9) / 10;
        return rating.toFixed(1);
    };

    const rating = getRating(locality.name);
    const reviewsCount = (locality.name.length * 3 + projectsCount * 2 + 5);

    return (
        <div className="container mx-auto px-4 pt-40 pb-20 max-w-7xl">
            <div className="flex flex-col lg:flex-row gap-12">
                {/* Main Content - Left Side */}
                <div className="flex-1">
                    {/* Title & Description */}
                    <div className="mb-10">
                        <h1 className="text-[2.6rem] font-bold text-black leading-tight mb-4">
                            Property Rates in {locality.name}, {locality.city?.name || locality.city || 'Ahmedabad'}
                        </h1>
                        <div className="flex items-center gap-1 text-black font-bold text-lg mb-8">
                            Showing for <button className="flex items-center gap-2 hover:opacity-70 transition-opacity">Last 5 Years <i className="fa-solid fa-chevron-down text-sm mt-1"></i></button>
                        </div>
                        
                        <div className="max-w-3xl mb-12">
                            <p className="text-black font-bold text-[1.1rem] leading-relaxed">
                                {locality.name}, {locality.city?.name || locality.city || 'Ahmedabad'} is one of the popular areas in the city. This area of the city has over {projectsCount} properties. ... <button className="text-black underline font-black hover:opacity-70 transition-opacity">Read more</button>
                            </p>
                        </div>

                        <div className="mb-12">
                            <button className="bg-black border border-black text-white font-black px-8 py-3 rounded-full text-[1.1rem] hover:opacity-80 transition-all uppercase tracking-widest">
                                Flat/Apartment
                            </button>
                        </div>
                    </div>

                    {/* Chart Section */}
                    <div className="mt-8 border-b border-slate-100 pb-20">
                        <div className="h-[450px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={yearlyChartData} margin={{ left: 0, right: 0, top: 20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#000" stopOpacity={0.05}/>
                                    <stop offset="95%" stopColor="#000" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                            <XAxis 
                                dataKey="name" 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{ fill: '#000', fontSize: 13, fontWeight: 700 }} 
                                padding={{ left: 50, right: 50 }}
                                dy={15}
                            />
                            <YAxis 
                                hide 
                                domain={['dataMin - 500', 'dataMax + 500']}
                            />
                            <Tooltip 
                                contentStyle={{ 
                                    borderRadius: '12px', 
                                    border: '1px solid #F1F5F9', 
                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', 
                                    padding: '12px 16px' 
                                }}
                                itemStyle={{ color: '#000', fontWeight: '900', fontSize: '14px' }}
                                labelStyle={{ color: '#000', opacity: 0.5, marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}
                                formatter={(value: any) => [`₹${Number(value).toLocaleString('en-IN')}`, 'Price / Sq.Ft']}
                            />
                            <Area 
                                type="monotone" 
                                dataKey="price" 
                                stroke="#000" 
                                strokeWidth={4} 
                                fillOpacity={1} 
                                fill="url(#colorPrice)"
                                dot={{ r: 5, fill: '#FFF', stroke: '#000', strokeWidth: 3 }}
                                activeDot={{ r: 7, fill: '#000', stroke: '#FFF', strokeWidth: 3 }}
                                animationDuration={1500}
                            />
                        </AreaChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="h-px bg-[#E2E8F0] w-full -mt-[1px]"></div>
                    </div>
                </div>

                {/* Sidebar - Right Side */}
                <div className="w-full lg:w-[400px] space-y-8">
                    {/* 1. Locality Stats Card */}
                    <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-sm">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-14 h-14 bg-orange-50 rounded-full flex items-center justify-center">
                                <i className="fa-solid fa-location-dot text-blue-600 text-2xl"></i>
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-black mb-1">{locality.name}</h3>
                                <div className="flex items-center gap-2">
                                    <span className="flex items-center gap-1 text-black font-black">
                                        <i className="fa-solid fa-star text-emerald-500"></i> {rating}
                                    </span>
                                    <span className="text-black font-bold text-sm underline opacity-60">{reviewsCount} Resident reviews</span>
                                </div>
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="flex items-center gap-2 text-black font-black text-xs uppercase tracking-widest">
                                <span className="opacity-40">Prices |</span>
                                <span>Buy - Flat/Apartment</span>
                                <i className="fa-solid fa-chevron-down text-[10px] mt-1"></i>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <p className="text-[10px] font-black text-black uppercase tracking-widest mb-1">Avg. Property Rate</p>
                                <p className="text-[14px] font-black text-black mb-2">₹{locality.avgPricePerSqFt.toLocaleString('en-IN')}/ sq.ft.</p>
                                <button className="text-blue-600 font-bold text-[11px] underline">View {projectsCount} projects</button>
                            </div>
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                <p className="text-[10px] font-black text-black uppercase tracking-widest mb-1">2 BHK</p>
                                <p className="text-[14px] font-black text-black mb-2">{twoBHKPrice}</p>
                                <button className="text-blue-600 font-bold text-[11px] underline">View properties</button>
                            </div>
                        </div>
                    </div>

                    {/* 2. Post Property Ad Card */}
                    <div className="relative bg-[#E8F5F1] rounded-2xl overflow-hidden p-8 border border-[#D1E9E2]">
                        <div className="relative z-10 max-w-[210px]">
                            <h3 className="text-[1.6rem] font-black text-black leading-[1.2] mb-3">Sell or rent faster at the right price!</h3>
                            <p className="text-black font-bold mb-8 text-[14px]">List your property now</p>
                            <button className="bg-[#0078DB] text-white font-black px-8 py-3.5 rounded-lg text-xs uppercase tracking-widest shadow-xl shadow-blue-200/50 hover:bg-blue-700 transition-all">
                                Post Property, It's FREE
                            </button>
                        </div>
                        {/* Image decoration */}
                        <div className="absolute right-[-10px] bottom-[-10px] w-[180px] h-[220px]">
                            <img 
                                src="file:///C:/Users/Hiren/.gemini/antigravity/brain/7078c0ce-24c3-4979-be09-f0c701e10333/post_property_ad_person_1774534641818.png" 
                                alt="Post Property" 
                                className="w-full h-full object-contain object-bottom transition-transform duration-500 hover:scale-105"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PriceTrendsHeader;
